import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSharedProfile } from '../context/SharedProfileContext';
import SharedProfileNavigation from './SharedProfileNavigation';
import ProfileSummaryHeader from './ProfileSummaryHeader';
import CompactProfileHeader from './CompactProfileHeader';
import ConversionCTA from './ConversionCTA';
import GeniLoader from './GeniLoader';
import { publicApi } from '../lib/api';

/**
 * PublicProfileWrapper
 * 
 * This component wraps real page components (Dashboard, OpenPositions, etc.)
 * when accessed via a PUBLIC PROFILE URL (/profile/{userId}/...).
 * 
 * PUBLIC PROFILE UX (Phase 1):
 * - Shows "Trading Performance" header with trader's name
 * - Shows "Public Profile • Read only" indicator (discrete)
 * - NO large "Viewing Shared Profile" banner
 * - Simplified navigation: Overview, Positions, Trades, Analytics, Performance, About
 * - Single clear CTA: "Start Your Journal"
 * - Clean, professional, premium feel
 * 
 * Unlike SharedProfileWrapper (legacy, share_id based), this uses:
 * - User's public_profile_enabled setting
 * - Stable URL based on user_id
 * - X-Public-Profile-ID header
 */
export default function PublicProfileWrapper({ children }) {
  const { userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { 
    enterPublicProfileMode, 
    exitPublicProfileMode, 
    isPublicProfileView,
    profileInfo 
  } = useSharedProfile();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ownerAnalytics, setOwnerAnalytics] = useState(null);

  // Determine if this is the Overview page (full header) or internal page (compact header)
  const isOverviewPage = location.pathname.endsWith('/dashboard') || location.pathname === `/profile/${userId}`;

  useEffect(() => {
    const initPublicProfile = async () => {
      if (!userId) {
        setError('No user ID provided');
        setLoading(false);
        return;
      }

      // If we're already in public profile mode for this same userId, skip re-initialization
      // This prevents flicker when navigating between tabs
      if (isPublicProfileView && profileInfo?.user_id === userId) {
        setLoading(false);
        return;
      }

      try {
        // Fetch public profile info and validate it's enabled
        // This is a PUBLIC endpoint - no auth required
        // Uses publicApi to bypass auth interceptor
        const response = await publicApi.get(`/public/profile/${userId}/info`);
        const data = response.data;
        
        if (!data || !data.profile_info) {
          throw new Error('Invalid profile data');
        }

        // Enter public profile mode with the userId
        // This sets the X-Public-Profile-ID header on all API requests
        enterPublicProfileMode(
          userId,
          data.profile_info
        );

        // Store owner analytics for the header
        setOwnerAnalytics(data.analytics || null);
        setLoading(false);
        
      } catch (err) {
        console.error('Error initializing public profile:', err);
        
        const status = err.response?.status;
        const detail = err.response?.data?.detail;
        
        if (status === 404) {
          // User not found
          setError('This user profile does not exist.');
        } else if (status === 403) {
          // Profile exists but is not public
          setError('This profile is not public.');
        } else if (status === 401) {
          // User not authenticated - redirect to login with return URL
          navigate(`/login?redirect=/profile/${userId}/dashboard`);
          return;
        } else {
          // Other error
          setError(detail || 'Failed to load profile. Please try again later.');
        }
        setLoading(false);
      }
    };

    initPublicProfile();

    // NOTE: We intentionally do NOT call exitPublicProfileMode() on unmount here.
    // Public profile mode should persist across tab navigation within the same profile.
    // The mode will be cleared when:
    // 1. User navigates completely away from public profile routes
    // 2. App component detects route change to non-public-profile path
    // 3. User logs in and goes to their own dashboard
  }, [userId, enterPublicProfileMode, navigate, isPublicProfileView, profileInfo]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <GeniLoader size="md" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <div className="text-6xl mb-6">🔒</div>
          <h2 className="text-2xl font-bold text-white mb-3">Profile Not Available</h2>
          <p className="text-white/60 mb-6">{error}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-3 bg-white text-black font-bold rounded-sm hover:bg-gray-200 transition-colors"
          >
            Go to Your Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Get the profile display name
  // Priority: public_profile_title > username > "Trader"
  const profileDisplayName = profileInfo?.public_profile_title || profileInfo?.username || 'Trader';

  // Render the wrapped component with public profile chrome
  return (
    <>
      {/* Navigation (no banner above it now) */}
      <SharedProfileNavigation isPublicProfile={true} profileUserId={userId} />
      
      {/* Main content area - pt-20 for nav (~56px) + breathing room */}
      <div className={`pt-20 ${isOverviewPage ? 'pb-20' : 'pb-24'}`}>
        {/* Profile Header - Full on Overview, Compact on internal pages */}
        {ownerAnalytics && isOverviewPage && (
          <div className="w-full max-w-[1600px] mx-auto px-4 md:px-8 pt-8">
            <ProfileSummaryHeader 
              analytics={ownerAnalytics} 
              username={profileDisplayName}
              isPublicProfile={true}
              avatarUrl={profileInfo?.public_profile_avatar_url}
            />
          </div>
        )}
        
        {/* Compact header for internal pages (Analytics, Trades, About) */}
        {ownerAnalytics && !isOverviewPage && (
          <div className="w-full max-w-[1600px] mx-auto px-4 md:px-8 pt-6">
            <CompactProfileHeader 
              username={profileDisplayName}
              avatarUrl={profileInfo?.public_profile_avatar_url}
            />
          </div>
        )}
        
        {/* Render the actual page component */}
        {children}
        
        {/* Bottom spacing for CTA - only on Overview to avoid extra scroll */}
        {isOverviewPage && <div className="h-8" />}
      </div>
      
      {/* Conversion CTA at bottom (fixed) */}
      <ConversionCTA />
    </>
  );
}
